/**
 * F3 — NIPE pending-writes tray.
 *
 * Lives in the Win95 taskbar next to NipeTrayIcon. Renders a badge with
 * the number of pending SP-write proposals for the current portal, and
 * a dropdown panel that lists each row with Approve / Reject / Preview.
 *
 * The actual Graph write fires here, after the user approves: read the
 * cached body, putContent with WRITE_POLICY, then markCompleted +
 * audit (paired demo). NIPE never writes directly.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { useFeatureFlags } from '../../lib/tenant/useFeatureFlags';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { useShowcaseSessionId } from '../../lib/showcase/showcaseSession';
import { useShowcaseVisible } from '../../lib/showcase/useShowcaseVisible';
import {
  clearPendingWriteBody,
  getAutoApproveAttribution,
  getPendingWriteBody,
  hashBody,
} from '../../lib/nipe/pendingWriteBodies';
import { getItemByPath, putContentByPath } from '../../lib/graph/sharepoint';
import { usePortalDrives, usePortalSites } from '../../lib/tenant/usePortalSharePoint';
import { pushGraphErrorToast } from '../../lib/graph/useGraphErrorToast';
import { subscribeTestPendingWrites } from '../../lib/test-hooks';
import './NipePendingWritesTray.css';

type PendingWriteRow = {
  _id: string;
  clientSlug: string;
  kind: 'sp-create-doc';
  folderPath: string;
  fileName: string;
  bodyHash: string;
  bodyPreview: string;
  summary: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'failed';
  createdAt: string;
  decidedAt?: string;
  completedAt?: string;
  errorMessage?: string;
};

/**
 * Detect `?test=1` so the J nipe-tray spec can render the tray + its
 * synthetic injected rows without needing portal scope. Returns the
 * synthetic clientSlug used by the test harness, or `''` to indicate
 * we should fall back to the normal portal-scope gate.
 */
function testSlugOverride(): string {
  if (typeof window === 'undefined') return '';
  try {
    if (new URLSearchParams(window.location.search).get('test') !== '1') return '';
    return (
      new URLSearchParams(window.location.search).get('clientSlug') ?? 'test'
    );
  } catch {
    return '';
  }
}

export function NipePendingWritesTray(): ReactElement | null {
  const flags = useFeatureFlags();
  const [isDemo] = useShowcaseVisible();
  const showcaseSessionId = useShowcaseSessionId();

  const testSlug = testSlugOverride();
  const clientSlug = useResolvedPortalSlug() ?? testSlug;
  // In test mode (?test=1) we force-render so specs can inject
  // synthetic rows; production still gates on `nipeEnabled && clientSlug`.
  const enabled = testSlug ? true : flags.nipeEnabled && clientSlug;

  // Live Convex query — runs in both production AND ?test=1 mode so
  // specs that inject via __repancyTestInjectNipeProposal (which
  // writes real Convex rows) can see their data in the tray. Demo
  // mode passes showcaseSessionId; non-demo (incl. test mode) reads the
  // user-scoped live table.
  const rows = useQuery(
    api.nipePendingWrites.listForSlug,
    enabled
      ? {
          clientSlug,
          ...(isDemo ? { demoSessionId: showcaseSessionId } : {}),
        }
      : 'skip',
  ) as PendingWriteRow[] | undefined;

  // J nipe-tray spec — merge in synthetic rows injected via
  // `__repancyTestInjectPendingWrite`. Subscriber returns empty
  // outside `?test=1`.
  const [syntheticRows, setSyntheticRows] = useState<PendingWriteRow[]>([]);
  useEffect(() => {
    const unsub = subscribeTestPendingWrites((tr) => {
      // Cast the synthetic shape to the production PendingWriteRow
      // shape — they share the four fields the tray actually reads
      // (_id, summary, folderPath, fileName + a placeholder kind/status).
      setSyntheticRows(
        tr.map((r) => ({
          _id: r._id,
          clientSlug: testSlugOverride() || 'test',
          kind: 'sp-create-doc',
          folderPath: r.folderPath,
          fileName: r.fileName,
          bodyHash: '',
          bodyPreview: r.bodyPreview,
          summary: r.summary,
          status: r.status,
          createdAt: r.createdAt,
        }) as PendingWriteRow),
      );
    });
    return unsub;
  }, []);

  if (!enabled) return null;
  const mergedRows = [...(rows ?? []), ...syntheticRows];
  return (
    <NipePendingWritesTrayInner
      clientSlug={clientSlug}
      isDemo={isDemo}
      showcaseSessionId={isDemo ? showcaseSessionId : undefined}
      rows={mergedRows}
    />
  );
}

interface InnerProps {
  clientSlug: string;
  isDemo: boolean;
  showcaseSessionId?: string;
  rows: PendingWriteRow[];
}

const AUTO_OPEN_STORAGE_KEY = 'repancy-nipe-pending-writes-auto-open-disabled';

function NipePendingWritesTrayInner({
  clientSlug,
  isDemo,
  showcaseSessionId,
  rows,
}: InnerProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // H.2 — track which ids we've already surfaced so a re-render or
  // refresh doesn't trigger a duplicate pulse or auto-open or toast.
  const seenPendingIdsRef = useRef<Set<string>>(new Set());
  const seenTerminalIdsRef = useRef<Map<string, PendingWriteRow['status']>>(new Map());
  const hasBootstrappedRef = useRef(false);

  const decide = useMutation(api.nipePendingWrites.decide);
  const markCompleted = useMutation(api.nipePendingWrites.markCompleted);
  const markFailed = useMutation(api.nipePendingWrites.markFailed);
  const auditNipeSp = useMutation(api.nipeSharePointAuditLog.logEvent);

  // Resolve the first portal site + drive for the SP write target.
  // Demo mode skips MSAL — writes fail loudly so the demo doesn't
  // silently no-op.
  const sitesState = usePortalSites();
  const firstSiteId =
    sitesState.kind === 'ok' && sitesState.value.length > 0
      ? sitesState.value[0]!.id ?? null
      : null;
  const drivesState = usePortalDrives(firstSiteId);
  const firstDriveId =
    drivesState.kind === 'ok' && drivesState.value.length > 0
      ? drivesState.value[0]!.id
      : null;

  const pending = useMemo(() => rows.filter((r) => r.status === 'pending'), [rows]);
  const recent = useMemo(
    () =>
      rows
        .filter((r) => r.status !== 'pending')
        .slice(0, 5),
    [rows],
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // H.2 — detect new proposals → pulse the badge + (optionally) auto-open
  // the panel on first arrival so users discover the surface. Detection
  // runs after a one-tick bootstrap so initial mounts don't pulse for
  // historical rows.
  useEffect(() => {
    const currentPendingIds = new Set(pending.map((p) => p._id));
    if (!hasBootstrappedRef.current) {
      seenPendingIdsRef.current = currentPendingIds;
      hasBootstrappedRef.current = true;
      // F-NIPE-TRAY-BOOTSTRAP-MARKER — let the J nipe-tray spec's
      // H.2 auto-open opt-out case wait for this flag before
      // injecting, so the "new row arrived after bootstrap" branch
      // fires deterministically. Self-gated on ?test=1 — production
      // builds without the query param never set the flag.
      if (
        typeof window !== 'undefined' &&
        (() => {
          try {
            return new URLSearchParams(window.location.search).get('test') === '1';
          } catch {
            return false;
          }
        })()
      ) {
        (window as unknown as { __repancyTestTrayBootstrapped?: boolean }).__repancyTestTrayBootstrapped = true;
      }
      return;
    }
    let hasNew = false;
    for (const id of currentPendingIds) {
      if (!seenPendingIdsRef.current.has(id)) {
        hasNew = true;
        break;
      }
    }
    if (!hasNew) {
      seenPendingIdsRef.current = currentPendingIds;
      return;
    }
    setPulsing(true);
    const autoOpenDisabled =
      typeof window !== 'undefined' &&
      window.localStorage?.getItem(AUTO_OPEN_STORAGE_KEY) === '1';
    if (!autoOpenDisabled && !open) {
      setOpen(true);
    }
    seenPendingIdsRef.current = currentPendingIds;
    const t = setTimeout(() => setPulsing(false), 6000);
    return () => clearTimeout(t);
  }, [pending, open]);

  // H.2 — toast on terminal transitions so users know the write
  // actually landed. Uses pushGraphErrorToast as the bus (already
  // mounted at app root); the message body discriminates by status.
  useEffect(() => {
    if (!hasBootstrappedRef.current) return;
    for (const row of rows) {
      if (row.status === 'pending') continue;
      const prevStatus = seenTerminalIdsRef.current.get(row._id);
      if (prevStatus === row.status) continue;
      seenTerminalIdsRef.current.set(row._id, row.status);
      // Don't re-toast on rows we just bootstrapped on mount.
      if (prevStatus === undefined && !hasBootstrappedRef.current) continue;
      switch (row.status) {
        case 'completed':
          pushGraphErrorToast(
            new Error(`Saved to SharePoint: ${row.fileName}.`),
          );
          break;
        case 'failed':
          pushGraphErrorToast(
            new Error(
              `SharePoint save failed: ${row.fileName}${row.errorMessage ? ` — ${row.errorMessage}` : ''}`,
            ),
          );
          break;
        case 'rejected':
          pushGraphErrorToast(
            new Error(`Rejected NIPE proposal: ${row.fileName}.`),
          );
          break;
        case 'approved':
          // Approved is a transient state on the way to completed;
          // toasting it would double-notify. Track silently.
          break;
      }
    }
  }, [rows]);

  // Clear pulse when the user opens the panel.
  useEffect(() => {
    if (open) setPulsing(false);
  }, [open]);

  const auditSp = useCallback(
    async (
      method: string,
      argsJson: string,
      outcome: 'ok' | 'error',
      detail?: string,
      // B.6 — optional rule attribution for auto-approved writes.
      autoApprove?: { ruleId: string; ruleLabel: string } | null,
    ) => {
      try {
        await auditNipeSp({
          clientSlug,
          method,
          args: argsJson,
          outcome,
          ...(detail ? { detail: detail.slice(0, 200) } : {}),
          ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
          ...(autoApprove
            ? {
                autoApprovedByRuleId: autoApprove.ruleId,
                autoApprovedByRuleLabel: autoApprove.ruleLabel,
              }
            : {}),
        });
      } catch {
        // audit best-effort
      }
    },
    [auditNipeSp, clientSlug, showcaseSessionId],
  );

  const handleApprove = useCallback(
    async (row: PendingWriteRow) => {
      const body = getPendingWriteBody(row._id);
      const argsJson = JSON.stringify({
        folderPath: row.folderPath,
        fileName: row.fileName,
      });
      if (body === null) {
        await decide({
          pendingWriteId: row._id,
          decision: 'approved',
          ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
        });
        await markFailed({
          pendingWriteId: row._id,
          errorMessage: 'Body cache empty (tab refresh or different session). Re-ask NIPE to propose the document.',
          ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
        });
        await auditSp('proposeSharePointDocument:approve', argsJson, 'error', 'body-cache-miss');
        return;
      }
      // Integrity: re-hash and compare. If the hash drifts the body has
      // been corrupted in cache; fail fast.
      const hash = await hashBody(body);
      if (hash !== row.bodyHash) {
        await decide({
          pendingWriteId: row._id,
          decision: 'approved',
          ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
        });
        await markFailed({
          pendingWriteId: row._id,
          errorMessage: 'Body hash mismatch — cached body changed since proposal.',
          ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
        });
        await auditSp('proposeSharePointDocument:approve', argsJson, 'error', 'hash-mismatch');
        return;
      }
      // Mark approved up-front; the write then transitions to completed
      // or failed below.
      await decide({
        pendingWriteId: row._id,
        decision: 'approved',
        ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
      });
      // B.6 — read auto-approve attribution (set by useNipeChat when
      // a rule matched). Null for manual approvals.
      const autoApprove = getAutoApproveAttribution(row._id);
      if (isDemo || !firstDriveId) {
        // No live Graph in demo. Mark completed with a synthetic note;
        // the demo tray is the surface the user sees, not the file.
        await markCompleted({
          pendingWriteId: row._id,
          ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
        });
        clearPendingWriteBody(row._id);
        await auditSp(
          'proposeSharePointDocument:approve',
          argsJson,
          'ok',
          'demo-no-graph-write',
          autoApprove,
        );
        return;
      }
      try {
        // Refuse to overwrite an existing file — NIPE-proposed docs
        // should never silently clobber a user file. If the parent
        // folder is missing, getItemByPath returns null and the PUT
        // below fails clearly.
        const folderItem = await getItemByPath(firstDriveId, row.folderPath || '');
        if (!folderItem) throw new Error(`Folder not found: ${row.folderPath || '(drive root)'}`);
        const childPath = row.folderPath ? `${row.folderPath}/${row.fileName}` : row.fileName;
        const child = await getItemByPath(firstDriveId, childPath);
        if (child) {
          throw new Error(`File already exists: ${childPath}`);
        }
        await putContentByPath(firstDriveId, row.folderPath, row.fileName, body);
        await markCompleted({
          pendingWriteId: row._id,
          ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
        });
        clearPendingWriteBody(row._id);
        await auditSp(
          'proposeSharePointDocument:approve',
          argsJson,
          'ok',
          'graph-put-ok',
          autoApprove,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        await markFailed({
          pendingWriteId: row._id,
          errorMessage: msg,
          ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
        });
        await auditSp('proposeSharePointDocument:approve', argsJson, 'error', msg);
      }
    },
    [decide, markCompleted, markFailed, showcaseSessionId, isDemo, firstDriveId, auditSp],
  );

  const handleReject = useCallback(
    async (row: PendingWriteRow) => {
      const argsJson = JSON.stringify({
        folderPath: row.folderPath,
        fileName: row.fileName,
      });
      await decide({
        pendingWriteId: row._id,
        decision: 'rejected',
        ...(showcaseSessionId ? { demoSessionId: showcaseSessionId } : {}),
      });
      clearPendingWriteBody(row._id);
      await auditSp('proposeSharePointDocument:reject', argsJson, 'ok');
    },
    [decide, showcaseSessionId, auditSp],
  );

  const tooltip =
    pending.length === 0
      ? 'NIPE will queue SharePoint saves here. None waiting.'
      : pending.length === 1
        ? '1 NIPE proposal needs your approval — click to review.'
        : `${pending.length} NIPE proposals need your approval — click to review.`;

  return (
    <div ref={containerRef} className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={tooltip}
        aria-expanded={open}
        data-testid={testIds.nipe.pendingWritesTrayButton}
        className={`nipe-pending-writes-tray-btn flex items-center justify-center h-7 px-2 rounded bg-[#ece9d8] border border-[#7f9db9] text-black text-[12px] leading-none cursor-pointer hover:bg-[#fff8d6]${
          pulsing ? ' nipe-pending-writes-tray-btn--pulsing' : ''
        }`}
        title={tooltip}
      >
        <span aria-hidden>{'✎'}</span>
        <span className="ml-1 hidden md:inline">Approve writes</span>
        {pending.length > 0 ? (
          <span
            data-testid={testIds.nipe.pendingWritesTrayBadge}
            className={`ml-1 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold${
              pulsing ? ' nipe-pending-writes-tray-badge--pulsing' : ''
            }`}
          >
            {pending.length}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="NIPE pending writes"
          data-testid={testIds.nipe.pendingWritesTrayPanel}
          className="absolute right-0 bottom-full mb-1 z-50 w-[22rem] bg-white text-black border-2 border-black shadow-[2px_2px_0_0_#808080] p-3 text-[12px]"
        >
          <div className="font-bold mb-2">Pending SharePoint writes</div>
          {pending.length === 0 ? (
            <div className="text-gray-700 mb-2">No proposals waiting for approval.</div>
          ) : (
            <ul className="m-0 p-0 list-none mb-2 max-h-72 overflow-y-auto">
              {pending.map((row) => (
                <PendingRow
                  key={row._id}
                  row={row}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
            </ul>
          )}
          {recent.length > 0 ? (
            <>
              <div className="font-bold mt-3 mb-1 text-gray-700">Recent</div>
              <ul className="m-0 p-0 list-none">
                {recent.map((row) => (
                  <li
                    key={row._id}
                    data-testid={testIds.nipe.pendingWriteRow(row._id)}
                    className="py-1 border-t border-gray-200"
                  >
                    <div className="truncate">{row.summary || row.fileName}</div>
                    <div
                      data-testid={testIds.nipe.pendingWriteStatus(row._id)}
                      className="text-gray-700"
                    >
                      {statusLabel(row.status)}
                      {row.errorMessage ? ` — ${row.errorMessage}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <AutoOpenToggle />
        </div>
      ) : null}
    </div>
  );
}

/**
 * H.2 — Lets the user opt out of auto-open on new proposals. Persists
 * the preference in localStorage so the choice survives reloads.
 */
function AutoOpenToggle(): ReactElement {
  const [disabled, setDisabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage?.getItem(AUTO_OPEN_STORAGE_KEY) === '1';
  });
  return (
    <label className="mt-3 pt-2 border-t border-gray-200 flex items-center gap-2 text-[11px] text-gray-700">
      <input
        type="checkbox"
        checked={disabled}
        data-testid={testIds.nipe.pendingWritesAutoOpenToggle}
        onChange={(e) => {
          const next = e.target.checked;
          setDisabled(next);
          try {
            if (next) window.localStorage?.setItem(AUTO_OPEN_STORAGE_KEY, '1');
            else window.localStorage?.removeItem(AUTO_OPEN_STORAGE_KEY);
          } catch {
            /* localStorage disabled */
          }
        }}
      />
      <span>Don't auto-open this tray on new proposals.</span>
    </label>
  );
}

function PendingRow({
  row,
  onApprove,
  onReject,
}: {
  row: PendingWriteRow;
  onApprove: (r: PendingWriteRow) => Promise<void>;
  onReject: (r: PendingWriteRow) => Promise<void>;
}): ReactElement {
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <li
      data-testid={testIds.nipe.pendingWriteRow(row._id)}
      className="py-2 border-t border-gray-200 first:border-t-0"
    >
      <div className="font-bold truncate">{row.fileName}</div>
      <div className="text-gray-800 mb-1 truncate" title={row.summary}>
        {row.summary}
      </div>
      <div className="text-gray-700 mb-2 truncate" title={row.folderPath}>
        Folder: {row.folderPath || '(drive root)'}
      </div>
      <div className="flex gap-1 mb-1">
        <button
          type="button"
          disabled={busy}
          data-testid={testIds.nipe.pendingWriteApprove(row._id)}
          onClick={async () => {
            setBusy(true);
            try {
              await onApprove(row);
            } finally {
              setBusy(false);
            }
          }}
          className="px-2 py-0.5 bg-[#ece9d8] border border-[#7f9db9] text-[12px] cursor-pointer disabled:opacity-50"
        >
          Approve
        </button>
        <button
          type="button"
          disabled={busy}
          data-testid={testIds.nipe.pendingWriteReject(row._id)}
          onClick={async () => {
            setBusy(true);
            try {
              await onReject(row);
            } finally {
              setBusy(false);
            }
          }}
          className="px-2 py-0.5 bg-[#ece9d8] border border-[#7f9db9] text-[12px] cursor-pointer disabled:opacity-50"
        >
          Reject
        </button>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="px-2 py-0.5 bg-[#ece9d8] border border-[#7f9db9] text-[12px] cursor-pointer"
        >
          {showPreview ? 'Hide' : 'Preview'}
        </button>
      </div>
      {showPreview ? (
        <pre
          data-testid={testIds.nipe.pendingWritePreview(row._id)}
          className="mt-1 max-h-40 overflow-y-auto bg-gray-100 border border-gray-400 p-2 whitespace-pre-wrap text-[11px] font-mono"
        >
          {row.bodyPreview}
        </pre>
      ) : null}
    </li>
  );
}

function statusLabel(s: PendingWriteRow['status']): string {
  switch (s) {
    case 'approved':
      return 'Approved · writing…';
    case 'completed':
      return 'Completed';
    case 'rejected':
      return 'Rejected';
    case 'failed':
      return 'Failed';
    default:
      return s;
  }
}
